import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import { useQuery } from "@msuite/ui/query";
import { Skeleton } from "@msuite/ui/skeleton";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { Tooltip } from "@msuite/ui/tooltip";
import { cn, responseHandler } from "@msuite/ui/utils";
import type { ContactMember } from "@msuite/utils/types";
import { client } from "@/hono";

interface BaseProps extends VariantProps<typeof contactMemberCard> {}

interface WithId extends BaseProps {
	contactMemberId: string;
	contactMember?: never;
}

interface WithContact extends BaseProps {
	contactMember: ContactMember;
	contactMemberId?: never;
}

type ContactMemberCardProps = WithId | WithContact;

export function ContactMemberCard({
	contactMemberId,
	contactMember,
	variant,
	fontSize,
	display,
}: ContactMemberCardProps) {
	/** Hooks */

	/** Classes */
	const classes = contactMemberCard({ fontSize, variant, display });

	/** Query (only when we have an ID and no full object) */
	const { data } = useQuery({
		queryKey: ["contact-member", contactMemberId ?? ""],
		queryFn: async () => {
			if (!contactMemberId) return null;
			const response = await client.contacts[":contactId"].members[":contactMemberId"].$get({
				param: {
					contactId: "unknown", // you’ll probably want to pass this properly
					contactMemberId,
				},
			});
			const json = await responseHandler(response);
			return json.contactMember as ContactMember;
		},
		enabled: !!contactMemberId && !contactMember,
	});

	/** Source of truth */
	const member = contactMember ?? data ?? null;

	/** Render */
	return (
		<div className={cn("flex-row items-center gap-2.5", classes)}>
			<Tooltip label={member?._type?.name}>
				<StatusIndicator color={member?._type?.color || "gray"} />
			</Tooltip>
			{member?.lastName ? (
				<p className="block overflow-ellipsis line-clamp-1 whitespace-nowrap">
					{`${member?.firstName ?? ""} ${member?.lastName ?? ""}`.trim()}
				</p>
			) : (
				<Skeleton placeholder="Markus Mustermann" />
			)}
		</div>
	);
}

const contactMemberCard = cva(["flex"], {
	variants: {
		variant: {
			default: ["card"],
			plain: [],
		},
		fontSize: {
			xs: ["text-xs"],
			sm: ["text-sm"],
			md: ["text-base"],
			lg: ["text-lg"],
		},
		display: {
			inline: ["inline-flex"],
		},
	},
	defaultVariants: {
		variant: "default",
		fontSize: "sm",
	},
});
